<template>
  <base-hm-input :id="id"
                 :class="[
                   $attrs['class'],
                   'has-inline-icon-left',
                   { 'has-inline-icon-right': hasSlot('icon-inline-right') },
                   { 'component__hm-input-multilang--open': open },
                 ]"
                 :label="label"
                 :help="help"
                 :has-focus="hasFocus"
                 :is-empty="isValueEmpty"
                 :name="name"
                 :cols="cols"
                 :hidden="hidden"
                 :style="iconCountStyleVariables"
                 :disabled="disabledCombined">
    <hm-select ref="langDropdown"
               v-model.trim="lang"
               :options="langs"
               :clearable="false"
               class="component__hm-select--underneath"
               label=""
               name="lang" >
      <template slot="option" slot-scope="props">
        <table>
          <tr>
            <td class="text--bold" v-html="props.option.id">
            </td>
            <td v-html="internalValue[props.option.id]"></td>
          </tr>
        </table>
      </template>
    </hm-select>
    <input v-if="lazy || disabledCombined"
           ref="input"
           :id="`${id}-input`"
           v-model.trim.lazy="internalValue[lang]"
           :data-vv-as="label"
           :data-vv-name="name"
           :name="name"
           :disabled="disabledCombined"
           :title="title"
           :autocomplete="autocomplete"
           :maxlength="maxlength"
           :type="type"
           :readonly="readonly"
           :class="['hm-input__input', 'hm-input__input--multilang', { 'privacy--blur': privacyBlur}]"
           @change="onChange($event)"
           @focus="onFocus"
           @blur="onBlur">
    <input v-else
           ref="input"
           :id="`${id}-input`"
           v-model.trim="internalValue[lang]"
           :data-vv-as="label"
           :data-vv-name="name"
           :name="name"
           :disabled="disabledCombined"
           :title="title"
           :autocomplete="autocomplete"
           :maxlength="maxlength"
           :type="type"
           :readonly="readonly"
           :class="['hm-input__input', 'hm-input__input--multilang', { 'privacy--blur': privacyBlur}]"
           @change="onChange($event)"
           @focus="onFocus"
           @blur="onBlur">
    <div :class="[{ 'hm-input__icon--input-hover': hover }, { 'hm-input__icon--input-focus': hasFocus}]" class="hm-input__icon__wrapper hm-input__icon__wrapper--left">
      <slot :hover="hover" :focus="hasFocus" name="icon-inline-left"></slot>
      <div class="hm-input__input__lang" @click="onClickLang" v-html="lang"></div>
    </div>
    <div :class="[{ 'hm-input__icon--input-hover': hover }, { 'hm-input__icon--input-focus': hasFocus}]" class="hm-input__icon__wrapper hm-input__icon__wrapper--right">
      <slot :hover="hover" :focus="hasFocus" name="icon-inline-right"></slot>
    </div>
  </base-hm-input>
</template>

<script>

import { isNil, isFunction, trim, isArray, head } from 'lodash';
import HmInputMixin from '@/mixins/HmInputMixin';
import HotelSettingsMixin from '@/mixins/HotelSettingsMixin';
import LocaleMixin from '@/mixins/LocaleMixin';

export default {
  name: 'HmInputMultilang',

  mixins: [LocaleMixin, HotelSettingsMixin, HmInputMixin],

  props: {
    value: {
      required: false,
      default: null,
    },
    type: {
      type: String,
      required: false,
      default: 'text',
    },
    autocomplete: {
      type: String,
      required: false,
      default: null,
    },
    maxlength: {
      type: Number,
      required: false,
      default: 255,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    lazy: {
      type: Boolean,
      default: false,
      required: false,
    },
    title: {
      type: String,
      required: false,
      default: null,
    },
    hasValue: {
      type: Boolean,
      required: false,
      default: false,
    },
    systemLanguages: {
      type: Boolean,
      required: false,
      default: false,
    },
    iconsRight: {
      type: Number,
      required: false,
      default: -1,
    },
    iconsLeft: {
      type: Number,
      required: false,
      default: -1,
    },
  },

  data: function () {
    return {
      lang: 'de',
      hover: false,
      files: null,
      open: false,
    };
  },

  computed: {
    preselectedLang: function () {
      const langs = this.langs.map(it => it.id);
      if (langs.includes(this.hotelSettings.details.primaryLanguage)) {
        return this.hotelSettings.details.primaryLanguage;
      } else if (langs.includes('en')) {
        return 'en';
      } else {
        return head(langs);
      }
    },
    langs: function () {
      if (this.systemLanguages === true) {
        return this.$systemLanguages.map(it => ({
          id: it,
          name: it,
        }));
      } else {
        const ret = [this.hotelSettings.details.primaryLanguage];
        this.hotelSettings.details.additionalLanguages.forEach(it => ret.push(it));
        return ret.map(it => ({
          id: it,
          name: it,
        }));
      }
    },
    isValueEmpty: function () {
      return (this.type === 'file' && this.hasValue === false) || (this.type !== 'file' && (isNil(this.value) || trim(this.value).length === 0));
    },
    iconCountLeft: function () {
      if (this.iconsLeft >= 0) return this.iconsLeft + 1;

      let slot = this.getSlot('icon-inline-left');
      slot = isNil(slot) || !isFunction(slot) ? slot : slot();

      if (isNil(slot)) return 0 + 1;
      else if (isArray(slot)) return slot.filter(it => typeof it.tag !== 'undefined').length + 1;
      else return 1 + 1;
    },
    iconCountRight: function () {
      if (this.iconsRight >= 0) return this.iconsRight;

      let slot = this.getSlot('icon-inline-right');
      slot = isNil(slot) || !isFunction(slot) ? slot : slot();

      if (isNil(slot)) return 0;
      else if (isArray(slot)) return slot.filter(it => typeof it.tag !== 'undefined').length;
      else return 1;
    },
    iconCountStyleVariables: function () {
      return {
        '--icons-left': this.iconCountLeft,
        '--icons-right': this.iconCountRight,
      };
    },
  },

  watch: {
    internalValue: function (newVal) {
      if (isNil(newVal)) {
        this.internalValue = {};
      }
    },
    hasFocus: function (newVal) {
      if (newVal === true) {
        this.$refs.langDropdown.$refs.autocomplete.open = false;
        this.open = this.$refs.langDropdown.$refs.autocomplete.open;
      }
    },
  },

  created: function () {
    this.lang = this.preselectedLang;

    if (isNil(this.value)) {
      this.internalValue = {};
    }
  },

  methods: {
    onChange($event) {
      if (this.type === 'file') {
        this.files = isNil(this.$refs.input) ? null : this.$refs.input.files;
      }
      this.$emit('change', $event);
    },
    onClickLang() {
      this.$refs.langDropdown.$refs.autocomplete.open = !this.$refs.langDropdown.$refs.autocomplete.open;
      this.open = this.$refs.langDropdown.$refs.autocomplete.open;
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
  @import '~styles/components/lib/HmInput';
  @import '~styles/import';

  .inline-button {
    @include icon-color($middlegray);
    @include icon-hover($text);
    &:hover {
      cursor: pointer;
    }
  }

  .component__hm-input-multilang {
    &--open {
      z-index: 10;
    }

    .inline-button {
      width: 3.2rem;

      @include icon-color($middlegray);
      @include icon-hover($text);
      &:hover {
        cursor: pointer;
      }
    }

    .hm-input__input__lang {
      pointer-events: all;
      cursor: pointer;

      &--disabled {
        pointer-events: none!important;
      }

      position: relative;
      &:before {
        content: ' ';
        display: block;
        width: 100%!important;
        height: 100%!important;
        top: 0.0rem!important;
        left: 0.1rem;
        position: absolute;
        background-repeat: no-repeat;
        background-size: contain;
        background-position: 0 0;
        background-image: url('~svg/switch.svg');
        pointer-events: none;
      }
    }
  }


</style>



// WEBPACK FOOTER //
// src/components/lib/inputs/HmInputMultilang.vue